import { useTranslation } from '@govard/i18n-core/react';
import { Alert, Card, InputNumber, Select, Spin, Switch, Typography } from 'antd';
import type { ReactElement } from 'react';

import {
  useConversionDigestMutation,
  useConversionDigestSettings,
} from '../hooks/useConversionDigest.js';

const { Text, Title } = Typography;

/**
 * Conversion digest settings: the SEPARATE consent contract (on/off, delivery
 * hour, stagnation days, language). Independent of the Telegram outbound
 * subscriptions above it — enabling it provisions the scheduled
 * "Conversion digest" automation in /automations and gates its Telegram
 * delivery; without consent the digest lands as a CRM note.
 */
export function ConversionDigestPanel(): ReactElement {
  const { t } = useTranslation();
  const query = useConversionDigestSettings();
  const mutation = useConversionDigestMutation();
  const data = query.data ?? {
    available: false,
    enabled: false,
    deliveryHour: 9,
    stagnationDays: 7,
    locale: 'ru' as const,
    chatBindingId: null,
    stateVersion: null,
  };

  const update = (patch: {
    enabled?: boolean;
    deliveryHour?: number;
    stagnationDays?: number;
    locale?: 'ru' | 'en';
  }) =>
    mutation.mutate({
      enabled: patch.enabled ?? data.enabled,
      deliveryHour: patch.deliveryHour ?? data.deliveryHour,
      stagnationDays: patch.stagnationDays ?? data.stagnationDays,
      locale: patch.locale ?? data.locale,
      expectedStateVersion: data.stateVersion,
    });

  if (query.isLoading) return <Spin aria-label={t('crm.settings.conversion_digest.loading')} />;
  if (query.isError)
    return <Alert type="error" title={t('crm.settings.conversion_digest.load_error')} showIcon />;

  return (
    <Card variant="borderless" aria-label={t('crm.settings.conversion_digest.title')}>
      <Title level={5}>{t('crm.settings.conversion_digest.title')}</Title>
      <Text type="secondary">{t('crm.settings.conversion_digest.description')}</Text>
      <Card size="small" className="mt-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Text strong>{t('crm.settings.conversion_digest.title')}</Text>
            <br />
            <Text type="secondary">{t('crm.settings.conversion_digest.enabled_hint')}</Text>
          </div>
          <Switch
            aria-label={t('crm.settings.conversion_digest.title')}
            checked={data.enabled}
            loading={mutation.isPending}
            onChange={(enabled) => update({ enabled })}
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <InputNumber
            aria-label={t('crm.settings.conversion_digest.hour')}
            min={0}
            max={23}
            precision={0}
            value={data.deliveryHour}
            disabled={mutation.isPending}
            onChange={(hour) => {
              if (hour !== null) update({ deliveryHour: hour });
            }}
          />
          <InputNumber
            aria-label={t('crm.settings.conversion_digest.stagnation_days')}
            min={1}
            max={90}
            precision={0}
            value={data.stagnationDays}
            disabled={mutation.isPending}
            onChange={(days) => {
              if (days !== null) update({ stagnationDays: days });
            }}
          />
          <Select
            aria-label={t('crm.settings.conversion_digest.language')}
            value={data.locale}
            disabled={mutation.isPending}
            options={[
              { value: 'ru', label: t('crm.settings.telegram.locale_ru') },
              { value: 'en', label: t('crm.settings.telegram.locale_en') },
            ]}
            onChange={(locale: 'ru' | 'en') => update({ locale })}
          />
        </div>
        {!data.available ? (
          <Alert
            className="mt-3"
            type="info"
            title={t('crm.settings.conversion_digest.telegram_unlinked')}
            showIcon
          />
        ) : null}
      </Card>
      {mutation.isError ? (
        <Alert
          className="mt-4"
          type="error"
          title={t('crm.settings.conversion_digest.save_error')}
          showIcon
        />
      ) : null}
    </Card>
  );
}
